import { assign, fromPromise, sendTo, setup } from 'xstate';
import { USER_STATE } from '~/features/xstateSystem';
import { XStateAxiosError } from '../../types';
import { sendAxiosErrorAlert } from '../../utils';
import { facilityMachine } from '../facility/facility.machine';
import { createFacilityViewState, onFacilityFormOpen } from '../facility/subMachineHelper';
import {
  addEducation,
  addTraining,
  closeDetailModal,
  closeEditTrainingModel,
  closeModalAndResetForms,
  deleteEduDetail,
  handleTrainingWizardNext,
  handleTrainingWizardPrev,
  openDeleteConfirmation,
  openDetailModal,
  openEditTrainingModel,
  openTrainingWizard,
  removeEducation,
  removeTraining,
  resetTrainingWizard,
  setEducationForm,
  setEducations,
  setIdToDelete,
  setModalToCreate,
  setModalToEdit,
  setSpecBoardOptions,
  setSpecCertOptions,
  setSubCertOptions,
  setTrainingForm,
  setTrainings,
  updatedEducation,
  updateFacilityForm,
  updateTrainings,
} from './actions';
import { deleteDetail, EDUCATION_API_CALLS, TRAINING_API_CALLS } from './api';
import { createEmptyEducationForm, createEmptyTrainingForm, emptyEducationForm } from './constants';
import { DeleteDetailEvent, Events } from './events';
import { AcademicMachineContext, Scope } from './types';

export const academicsMachine = setup({
  actors: {
    createEducation: fromPromise(EDUCATION_API_CALLS.create),
    getEducation: fromPromise(EDUCATION_API_CALLS.get),
    deleteEducation: fromPromise(EDUCATION_API_CALLS.delete),
    patchEducation: fromPromise(EDUCATION_API_CALLS.patch),
    deleteDetail: fromPromise(deleteDetail),
    getTraining: fromPromise(TRAINING_API_CALLS.get),
    createTraining: fromPromise(TRAINING_API_CALLS.create),
    deleteTraining: fromPromise(TRAINING_API_CALLS.delete),
    patchTraining: fromPromise(TRAINING_API_CALLS.patch),
    facilityRef: facilityMachine,
  },
  actions: {
    addEducation: assign(addEducation),
    closeDetailModal: assign(closeDetailModal),
    closeModal: assign(closeModalAndResetForms),
    deleteEduDetail: assign(deleteEduDetail),
    removeEducation: assign(removeEducation),
    openDetailModal: assign(openDetailModal),
    resetTrainingWizard: assign(resetTrainingWizard),
    setEducationForm: assign(setEducationForm),
    setEducations: assign(setEducations),
    setIdToDelete: assign(setIdToDelete),
    setModalToCreate: assign(setModalToCreate),
    setModalToEdit: assign(setModalToEdit),
    setTrainings: assign(setTrainings),
    setSpecBoardOptions: assign(setSpecBoardOptions),
    setSubCertOptions: assign(setSubCertOptions),
    setSpecCertOptions: assign(setSpecCertOptions),
    updateFacilityForm: assign(updateFacilityForm),
    updateEducation: assign(updatedEducation),
    addTraining: assign(addTraining),
    removeTraining: assign(removeTraining),
    closeEditTrainingModel: assign(closeEditTrainingModel),
    updateTrainings: assign(updateTrainings),
    openEditTrainingModel: assign(openEditTrainingModel),
    setTrainingForm: assign(setTrainingForm),
    handleTrainingWizardNext: assign(handleTrainingWizardNext),
    handleTrainingWizardPrev: assign(handleTrainingWizardPrev),
    openTrainingWizard: assign(openTrainingWizard),
    sendDeleteConfirmation: openDeleteConfirmation,
  },
  types: {
    context: {} as AcademicMachineContext,
    events: {} as Events,
    input: {} as Partial<AcademicMachineContext>,
  },
}).createMachine({
  context: ({ input, spawn }) => ({
    _idToDelete: -1,
    _specialtyBoardOptions: [],
    _specCertOptions: [],
    _specSubCertOptions: [],
    providerId: -1,
    trainingWizard: {
      open: false,
      currentPage: 1,
    },
    loading: false,
    educations: input.educations || [],
    facilityRef: spawn('facilityRef', {
      id: 'facilityStateAcademics',
      input: undefined,
    }),
    trainings: [],
    editId: -1,
    educationForm: emptyEducationForm,
    trainingForm: createEmptyTrainingForm(),
    currentTab: 0,
    modal: {
      edit: false,
      create: false,
      detailModal: false,
    },
    openEditTrainingModal: false,
    activeTab: 0,
  }),
  initial: '$_IDLE',
  on: {
    ON_CLOSE_MODAL: {
      target: '.$_IDLE',
      actions: ['closeModal', assign({ trainingForm: () => createEmptyTrainingForm() })],
    },
    ON_SET_PROVIDER_ID: {
      actions: [assign({ loading: true, providerId: ({ event }) => event.payload.providerId })],
      target: '.$_LOAD_EDUCATIONS',
    },
    ON_LOAD: [
      {
        guard: ({ context, event }) => event.payload.mode === 'education',
        actions: assign({ loading: true }),
        target: '.$_LOAD_EDUCATIONS',
      },
      {
        guard: ({ context, event }) => event.payload.mode === 'training',
        actions: assign({ loading: true }),
        target: '.$_LOAD_TRAININGS',
      },
    ],
    ON_DELETE: [
      {
        guard: ({ context, event }) => event.payload.type === 'EDUCATION',
        actions: assign({ loading: true }),
        target: '.$_SEND_DELETE_EDUCATION',
      },
      {
        guard: ({ context, event }) => event.payload.type === 'TRAINING',
        actions: assign({ loading: true }),
        target: '.$_SEND_DELETE_TRAINING',
      },
    ],
    ON_TAB_SWITCH: [
      {
        guard: ({ event }) => event.value == 0,
        actions: assign({ currentTab: 0, loading: true }),
        target: '.$_LOAD_EDUCATIONS',
      },
      {
        guard: ({ event }) => event.value == 1,
        target: '.$_LOAD_TRAININGS',
        actions: assign({ currentTab: 1, loading: true }),
      },
      {
        actions: assign({ currentTab: 0 }),
        target: '.$_LOAD_EDUCATIONS',
      },
    ],
  },
  states: {
    $_IDLE: {},
    $_LOAD_EDUCATIONS: {
      invoke: {
        id: 'fetchEducation',
        src: 'getEducation',
        input: (inputArgs) => inputArgs,
        onDone: {
          actions: ['closeModal', 'setEducations', assign({ loading: false })],
          target: '$_EDUCATION_VIEW',
        },
        onError: {
          actions: [
            'closeModal',
            assign({ loading: false, educationForm: () => createEmptyEducationForm() }),
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
    $_LOAD_TRAININGS: {
      invoke: {
        id: 'fetchTraining',
        src: 'getTraining',
        input: (inputArgs) => inputArgs,
        onDone: {
          actions: ['setTrainings', 'closeModal', assign({ loading: false })],
          target: '$_TRAINING_VIEW',
        },
        onError: {
          target: '$_TRAINING_VIEW',
          actions: [
            'closeModal',
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
            assign({ trainingForm: () => createEmptyTrainingForm(), loading: false }),
          ],
        },
      },
    },
    $_EDUCATION_VIEW: {
      on: {
        ON_ADD: {
          target: '$_EDUCATION_FORM',
          actions: [
            'setModalToCreate',
            // MCV-694: fresh factory — see createEmptyEducationForm note.
            assign({
              loading: false,
              educationForm: () => createEmptyEducationForm(),
            }),
          ],
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: 'sendDeleteConfirmation',
        },
        ON_DELETE: {
          target: '$_SEND_DELETE_EDUCATION',
          actions: ['setIdToDelete', assign({ loading: true })],
        },

        ON_EDIT: {
          target: '$_EDUCATION_FORM',
          actions: ['setModalToEdit', 'setEducationForm'],
        },
      },
    },
    $_EDUCATION_FORM: {
      initial: '$_IDLE',
      states: {
        $_IDLE: {},
        $_DETAIL_FORM: {},
        $_SEND_DELETE_DETAIL: {
          invoke: {
            id: 'deleteDetail',
            src: 'deleteDetail',
            input: (scope: Omit<Scope, 'system'>) => {
              const { payload } = scope.event as DeleteDetailEvent;
              return { id: payload.id };
            },
            onDone: {
              target: '..$_EDUCATION_FORM',
              actions: ['deleteEduDetail', assign({ loading: false })],
            },
            onError: {
              target: '..$_EDUCATION_FORM',
              actions: [
                assign({ loading: false }),
                sendTo(
                  ({ system }) => system.get('userState'),
                  ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
                ),
              ],
            },
          },
        },
      },
      on: {
        ON_SUBMIT: {
          target: '$_SEND_CREATE_EDUCATION',
        },
        ON_UPDATE: {
          target: '$_SEND_UPDATE_EDUCATION',
        },
        ON_CLOSE_MODAL: {
          target: '$_EDUCATION_VIEW',
          actions: ['closeModal'],
        },
        ON_VALIDATE: {
          target: '.$_DETAIL_FORM',
          actions: ['openDetailModal'],
        },
        ON_CLOSE_DETAIL_MODAL: {
          actions: ['closeDetailModal'],
          target: '$_EDUCATION_FORM',
        },
        ON_DELETE_DETAIL: {
          target: '.$_SEND_DELETE_DETAIL',
        },
      },
    },
    $_SEND_CREATE_EDUCATION: {
      invoke: {
        id: 'create',
        src: 'createEducation',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_EDUCATION_VIEW',
          actions: [
            'addEducation',
            'closeModal',
            // MCV-694: fresh factory — see createEmptyEducationForm note.
            assign({ loading: false, educationForm: () => createEmptyEducationForm() }),
          ],
        },
        // [MCV-1208] Return to $_EDUCATION_FORM (not $_EDUCATION_VIEW) so the user's
        // input is preserved and the dialog stays interactive. The previous target
        // left the modal visually open but the machine in a state that did not handle
        // save/close events, so the Save and Add Education buttons appeared to hang
        // until a full page refresh. Matches the MCV-873 AOI error-recovery pattern.
        onError: {
          target: '$_EDUCATION_FORM',
          actions: [
            assign({ loading: false }),
            sendTo(
              ({ system }) => system.get('userState'),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
    $_SEND_UPDATE_EDUCATION: {
      invoke: {
        id: 'patch',
        src: 'patchEducation',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_EDUCATION_VIEW',
          actions: ['updateEducation', 'closeModal', assign({ loading: false })],
        },
        onError: {
          target: '$_EDUCATION_VIEW',
          actions: [
            assign({ loading: false }),
            'closeModal',
            sendTo(
              ({ system }) => system.get('userState'),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
    $_SEND_DELETE_EDUCATION: {
      invoke: {
        id: 'deleteEducation',
        src: 'deleteEducation',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_LOAD_EDUCATIONS',
          actions: ['removeEducation'],
        },
        onError: {
          actions: [
            assign({ loading: false }),
            sendTo(
              ({ system }) => system.get(USER_STATE),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
    $_TRAINING_VIEW: {
      entry: [assign({ loading: false })],
      on: {
        ON_OPEN_WIZARD: {
          target: '$_TRAINING_WIZARD',
          actions: ['openTrainingWizard'],
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: 'sendDeleteConfirmation',
        },
        ON_DELETE: {
          target: '$_SEND_DELETE_TRAINING',
          actions: ['setIdToDelete', assign({ loading: true })],
        },
        ON_EDIT: {
          target: '$_TRAINING_FORM',
          actions: ['openEditTrainingModel', 'setTrainingForm'],
        },
      },
    },
    $_TRAINING_FORM: {
      on: {
        ON_CLOSE_MODAL: {
          target: '$_TRAINING_VIEW',
          actions: ['closeEditTrainingModel', 'resetTrainingWizard'],
        },
        ON_UPDATE: {
          target: '$_SEND_UPDATE_TRAINING',
        },
      },
    },
    ...createFacilityViewState('$_TRAINING_WIZARD', ['updateFacilityForm']),
    $_TRAINING_WIZARD: {
      on: {
        ON_CLOSE_MODAL: {
          target: '$_TRAINING_VIEW',
          actions: [
            'resetTrainingWizard',
            assign({ trainingForm: () => createEmptyTrainingForm() }),
          ],
        },
        ON_WIZ_NEXT_PAGE: {
          actions: ['handleTrainingWizardNext'],
        },
        ON_WIZ_PREV_PAGE: {
          target: '$_TRAINING_WIZARD',
          actions: ['handleTrainingWizardPrev'],
        },
        ON_SET_SPEC_BOARD_OPTIONS: {
          target: '$_TRAINING_WIZARD',
          actions: ['setSpecBoardOptions'],
        },
        ON_SET_SPEC_CERT_OPTIONS: {
          target: '$_TRAINING_WIZARD',
          actions: ['setSpecCertOptions'],
        },
        ON_SET_SUB_SPEC_CERT_OPTIONS: {
          target: '$_TRAINING_WIZARD',
          actions: ['setSubCertOptions'],
        },
        ON_SUBMIT: {
          target: '$_SEND_CREATE_TRAINING',
        },
        ...onFacilityFormOpen(),
      },
    },
    $_SEND_CREATE_TRAINING: {
      invoke: {
        id: 'trainingCreate',
        src: 'createTraining',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_TRAINING_VIEW',
          actions: [
            'closeModal',
            'resetTrainingWizard',
            'addTraining',
            assign({
              loading: false,
              trainingForm: () => createEmptyTrainingForm(),
            }),
          ],
        },
        onError: {
          target: '$_TRAINING_VIEW',
          actions: [
            'closeModal',
            'resetTrainingWizard',
            assign({
              loading: false,
              trainingForm: () => createEmptyTrainingForm(),
            }),

            sendTo(
              ({ system }) => system.get('userState'),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
    $_SEND_DELETE_TRAINING: {
      invoke: {
        id: 'deleteTraining',
        src: 'deleteTraining',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_TRAINING_VIEW',
          actions: ['removeTraining'],
        },
        onError: {
          target: '$_TRAINING_VIEW',
          actions: [
            assign({ loading: false }),
            sendTo(
              ({ system }) => system.get('userState'),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
    $_SEND_UPDATE_TRAINING: {
      invoke: {
        id: 'patch',
        src: 'patchTraining',
        input: (inputArgs) => inputArgs,
        onDone: {
          target: '$_TRAINING_VIEW',
          actions: [
            'closeEditTrainingModel',
            'updateTrainings',
            assign({
              loading: false,
              trainingForm: () => createEmptyTrainingForm(),
            }),
          ],
        },
        onError: {
          target: '$_TRAINING_VIEW',
          actions: [
            'closeEditTrainingModel',
            assign({
              loading: false,
              trainingForm: () => createEmptyTrainingForm(),
            }),
            sendTo(
              ({ system }) => system.get('userState'),
              ({ event }) => sendAxiosErrorAlert(event as XStateAxiosError)
            ),
          ],
        },
      },
    },
  },
});
